import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo, { JsonLd } from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/news";
import { getArticlesByCategory } from "@/lib/news";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const list = getArticlesByCategory(slug);

  if (!category) {
    return (
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">未找到该分类</h1>
          <p className="mt-3 text-sm text-foreground-600 font-label">
            这个分类可能已经调整，先回到全部快讯看看吧。
          </p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回全部快讯
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 独家吃瓜`,
    description: category.desc,
    inLanguage: "zh-CN",
    url: `https://mvlock.cn/category/${category.slug}`,
    isPartOf: { "@type": "WebSite", name: "独家吃瓜" },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: list.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `https://mvlock.cn/article/${article.id}`,
        name: article.title,
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://mvlock.cn/" },
      { "@type": "ListItem", position: 2, name: "全部快讯", item: "https://mvlock.cn/news" },
      {
        "@type": "ListItem",
        position: 3,
        name: category.name,
        item: `https://mvlock.cn/category/${category.slug}`,
      },
    ],
  };

  return (
    <>
      <Seo
        title={`${category.name} - 独家吃瓜最新动态`}
        description={`独家吃瓜${category.name}专栏，${category.desc} 持续更新今日最新动态与现场直击，滚动播报不错过任何细节。`}
        keywords={`${category.name},独家吃瓜${category.name},娱乐快讯,${category.name}最新动态`}
        path={`/category/${category.slug}`}
      />
      <JsonLd data={collectionSchema} />
      <JsonLd data={breadcrumbSchema} />
      <SiteLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "全部快讯", to: "/news" }, { label: category.name }]} />

          <header className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8 mb-8">
            <div className="flex items-start gap-4">
              <span className="w-14 h-14 shrink-0 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-2xl">
                <i className={category.icon}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                  {category.name}
                </h1>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed font-label max-w-2xl">
                  {category.desc}
                </p>
                <p className="mt-3 text-xs text-foreground-500 font-label">
                  共收录 <span className="text-primary-600 font-medium">{list.length}</span> 条内容
                </p>
              </div>
            </div>
          </header>

          <div className="flex flex-wrap gap-2 mb-7">
            <Link
              to="/news"
              className="px-3.5 py-1.5 rounded-full text-xs font-label bg-background-50 border border-background-300 text-foreground-600 hover:border-primary-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              全部
            </Link>
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-label transition-colors whitespace-nowrap cursor-pointer ${
                  item.slug === category.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-300 text-foreground-600 hover:border-primary-300"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {list.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {list.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200">
              <h2 className="font-heading text-lg font-semibold text-foreground-900">
                该分类暂无内容
              </h2>
              <p className="mt-2 text-sm text-foreground-500 font-label">稍后再来看看吧。</p>
            </div>
          )}

          <div className="mt-10 p-6 rounded-lg bg-secondary-50 border border-secondary-200">
            <h2 className="font-heading text-lg font-semibold text-secondary-900 mb-3">
              其他分类推荐
            </h2>
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((item) => item.slug !== category.slug)
                .map((item) => (
                  <Link
                    key={item.slug}
                    to={`/category/${item.slug}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-background-50 border border-secondary-200 text-secondary-800 text-sm font-label hover:border-secondary-400 transition-colors cursor-pointer"
                  >
                    <i className={item.icon}></i>
                    {item.name}
                  </Link>
                ))}
            </div>
          </div>

          <div className="mt-10">
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
              最新快讯
            </h2>
            <div className="rounded-lg bg-background-100 border border-background-200 px-5">
              {articles.slice(0, 6).map((article, index) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  variant="compact"
                  rank={index + 1}
                />
              ))}
            </div>
          </div>
        </div>
      </SiteLayout>
    </>
  );
}